import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Factory, ArrowLeft, Lock, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.pexels.com/photos/8956445/pexels-photo-8956445.jpeg";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => nav("/login"), 2200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex flex-col px-6 py-8 lg:px-16">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-7 w-7 bg-primary grid place-items-center rounded-sm"><Factory className="h-4 w-4 text-primary-foreground" /></div>
          <span className="font-heading font-extrabold text-lg tracking-tight">Nepton</span>
        </Link>

        <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto">
          {!token ? (
            <div data-testid="reset-no-token">
              <div className="h-12 w-12 bg-amber-50 border border-amber-200 grid place-items-center rounded-sm"><AlertTriangle className="h-6 w-6 text-amber-600" /></div>
              <h1 className="mt-5 text-3xl font-bold tracking-tight">Invalid reset link</h1>
              <p className="mt-3 text-sm text-muted-foreground">This link is missing its reset token. Please request a new password reset.</p>
              <Link to="/forgot-password" className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-sm hover:bg-brand-dark transition-colors text-sm">Request new link</Link>
            </div>
          ) : done ? (
            <div data-testid="reset-success">
              <div className="h-12 w-12 bg-emerald-50 border border-emerald-200 grid place-items-center rounded-sm"><CheckCircle2 className="h-6 w-6 text-emerald-600" /></div>
              <h1 className="mt-5 text-3xl font-bold tracking-tight">Password reset</h1>
              <p className="mt-3 text-sm text-muted-foreground">Your password has been updated. Redirecting you to sign in…</p>
              <Link to="/login" className="mt-8 inline-flex items-center gap-1.5 text-sm text-brand hover:underline"><ArrowLeft className="h-4 w-4" /> Go to sign in now</Link>
            </div>
          ) : (
            <>
              <div className="label-tech text-brand mb-3">Set new password</div>
              <h1 className="text-3xl font-bold tracking-tight">Choose a new password</h1>
              <p className="mt-2 text-sm text-muted-foreground">Enter and confirm your new password below.</p>

              <form onSubmit={submit} className="mt-8 space-y-4">
                <label className="block">
                  <span className="label-tech text-muted-foreground block mb-1.5">New password</span>
                  <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" className="rp-input" placeholder="••••••••" />
                </label>
                <label className="block">
                  <span className="label-tech text-muted-foreground block mb-1.5">Confirm password</span>
                  <input required type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" className="rp-input" placeholder="••••••••" />
                </label>
                {error && <div data-testid="reset-error" className="text-sm text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2 rounded-sm">{error}</div>}
                <button type="submit" disabled={loading} data-testid="reset-submit"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-brand text-white rounded-sm hover:bg-brand-dark disabled:opacity-60 transition-colors">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                  {loading ? "Updating…" : "Reset password"}
                </button>
              </form>
              <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>
            </>
          )}
        </div>
      </div>

      <div className="hidden lg:block relative border-l border-border">
        <img src={HERO} alt="Manufacturing" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/70" />
      </div>

      <style>{`.rp-input{width:100%;border:1px solid hsl(var(--input));background:hsl(var(--card));padding:0.65rem 0.85rem;border-radius:0.25rem;font-size:0.9rem;outline:none;transition:border-color .15s}.rp-input:focus{border-color:#EA580C}`}</style>
    </div>
  );
}
